<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import logoUrl from '@/assets/login-logo.png';
import AuthLayout from "@/layout/auth/AuthLayout.vue";
import { Head, Link, useForm } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading.js';

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const { showLoading, hideLoading } = useLoading();
const submit = () => {
    showLoading($t('Signing in...'));
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => {
            form.reset('password');
            hideLoading();
        },
    });
};

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

defineOptions({
    layout: AuthLayout,
});

</script>

<template>
    <Head :title="'Admin - ' + $t('Login')" />

    <div class="border mx-2 sm:mx-auto px-5 sm:px-20 pb-16 rounded-2xl">
        <h1 class="text-4xl sm:text-5xl font-medium text-center mx-auto leading-[125%] my-12 sm:min-w-96">
            <img :src="logoUrl" class="h-16 w-auto mx-auto" alt="Logo"/>
        </h1>

        <form @submit.prevent="submit" class="max-w-[35rem] mx-auto">
            <div v-if="status" class="font-medium text-sm text-green-600 mb-4">
                {{ status }}
            </div>

            <div class="mb-6 flex flex-col gap-2">

                <FloatLabel variant="on">
                    <InputText
                        v-model="form.email"
                        type="email"
                        class="w-full"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Email') }} <span class="required-color">*</span></label>
                </FloatLabel>

                </div>

            <div class="flex flex-col gap-2">
                <FloatLabel variant="on">
                    <Password
                        v-model="form.password"
                        :feedback="false"
                        toggleMask
                        class="w-full"
                        :pt="{
                        pcInputText: {
                            root: {
                                class: '!w-full'
                            }
                        }
                    }"
                        required
                    />
                    <label for="locale" class="text-sm text-surface-500">{{ $t('Password') }} <span class="required-color">*</span></label>
                </FloatLabel>
                <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
            </div>

            <div class="space-y-8 mt-4 text-sm">
                <div class="flex items-center justify-between">
                    <Link
                        v-if="canResetPassword"
                        :href="route('password.request')"
                        class="text-primary-active hover:opacity-75 transition-all"
                    >
                        {{ $t('Forgot your password?') }}
                    </Link>
                </div>

                <div v-if="form.errors.email" class="text-red-500 text-sm text-center">
                    {{ form.errors.email }}
                </div>

                <Button
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    type="submit"
                    class="landing-button-primary w-full"
                >
                    {{ $t('Login') }}
                </Button>
            </div>
        </form>
    </div>
</template>

<style scoped>
.p-error {
    @apply text-red-500 text-xs mt-1;
}
</style>
